import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError, api } from "@/api/client";
import { useUnits } from "@/api/commerce";
import { downloadExport } from "@/api/crm";
import { useCountries, useMe } from "@/api/hooks";
import { useCompany, useProducts, useSaveProduct } from "@/api/sales";
import type { Product } from "@/api/types";
import { Badge } from "@/components/crm/Badge";
import { findCountry } from "@/components/crm/LocationFields";
import { Icon } from "@/components/icons";
import { Alert, EmptyState, Modal, PageHeader, Spinner, TextField } from "@/components/ui";
import { money } from "@/lib/format";
import { can } from "@/lib/permissions";

const UOMS = ["pcs", "nos", "set", "kg", "g", "tonne", "m", "mm", "ft", "l", "box", "roll", "pair", "hr"];
const GST_SLABS = [0, 5, 12, 18, 28];

/** Labels and the default rate follow the company's country (HSN and GST in India, HS code and VAT elsewhere). */
function useCatalogTerms() {
  const company = useCompany();
  const countries = useCountries();
  const c = findCountry(countries.data, company.data?.country);
  return {
    code: c?.item_code_label ?? "HS code", tax: c?.tax_name ?? "Tax", isIndia: c?.code === "IN",
    standardRate: c ? String(Number(c.standard_rate)) : "0",
  };
}

interface Values { name: string; sku: string; hsn_code: string; category: string; uom: string; unit_price: string; tax_rate: string; description: string; is_active: boolean; cost_price: string; min_margin_pct: string }
interface UnitRow { uom: string; factor: string }

function ProductForm({ product, onDone }: { product?: Product; onDone: () => void }) {
  const save = useSaveProduct(product?.id);
  const terms = useCatalogTerms();
  const pricing = can(useMe().data?.permissions ?? [], "cpq:manage");
  const savedUnits = useUnits(product?.id);
  const [units, setUnits] = useState<UnitRow[] | null>(null);
  const unitRows = units ?? savedUnits.data?.map((u) => ({ uom: u.uom, factor: String(u.factor) })) ?? [];
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit } = useForm<Values>({
    defaultValues: {
      name: product?.name ?? "", sku: product?.sku ?? "", hsn_code: product?.hsn_code ?? "", category: product?.category ?? "",
      uom: product?.uom ?? "pcs", unit_price: product ? String(product.unit_price) : "", tax_rate: product ? String(product.tax_rate) : terms.standardRate,
      description: product?.description ?? "", is_active: product?.is_active ?? true,
      cost_price: product?.cost_price != null ? String(product.cost_price) : "", min_margin_pct: product?.min_margin_pct != null ? String(product.min_margin_pct) : "",
    },
  });
  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setError(null);
    try {
      const saved = await save.mutateAsync({
        name: v.name.trim(), sku: v.sku.trim(), hsn_code: v.hsn_code.trim() || null, category: v.category.trim() || null,
        uom: v.uom, unit_price: v.unit_price || "0", tax_rate: v.tax_rate, description: v.description.trim() || null, is_active: v.is_active,
        ...(pricing ? { cost_price: v.cost_price || null, min_margin_pct: v.min_margin_pct || null } : {}),
      });
      if (pricing && units !== null) {
        await api("PUT", `/products/${saved.id}/units`, units.filter((u) => u.uom.trim() && Number(u.factor) > 0).map((u) => ({ uom: u.uom.trim(), factor: u.factor })));
      }
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else if (e instanceof ApiError && e.status === 409) setErrors({ sku: e.message });
      else setError(e instanceof Error ? e.message : "Could not save");
    }
  });
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {error && <Alert>{error}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><TextField label="Product name *" {...register("name")} error={errors.name} /></div>
        <TextField label="SKU / part number *" {...register("sku")} error={errors.sku} />
        <TextField label={terms.code} inputMode="numeric" hint={terms.isIndia ? "4 to 8 digits" : "Tariff code, 4 to 10 digits"} {...register("hsn_code")} error={errors.hsn_code} />
        <TextField label="Unit price" type="number" min={0} step="0.01" {...register("unit_price")} error={errors.unit_price} />
        <div>
          <label htmlFor="tax_rate" className="label">{terms.tax} rate</label>
          {terms.isIndia
            ? <select id="tax_rate" className="input" {...register("tax_rate")}>{GST_SLABS.map((g) => <option key={g} value={g}>{g}%</option>)}</select>
            : <input id="tax_rate" className="input" type="number" min={0} max={100} step="0.001" {...register("tax_rate")} aria-invalid={!!errors.tax_rate} />}
          {errors.tax_rate ? <p className="field-error">{errors.tax_rate}</p> : <p className="mt-1 text-xs text-muted">Standard rate here is {terms.standardRate}%. The buyer's location can still change the tax on a quote.</p>}
        </div>
        <div>
          <label htmlFor="uom" className="label">Unit</label>
          <select id="uom" className="input" {...register("uom")}>{UOMS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
        </div>
        <TextField label="Category" {...register("category")} error={errors.category} />
        <div className="sm:col-span-2">
          <label htmlFor="description" className="label">Description</label>
          <textarea id="description" className="input min-h-20" {...register("description")} />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-brand" {...register("is_active")} />Active (available on quotes)</label>
      </div>
      {pricing && (
        <fieldset className="space-y-3 rounded-md border border-line p-4">
          <legend className="px-1 text-sm font-semibold">Cost, margin and units <span className="font-normal text-muted">(only people who manage pricing see this)</span></legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Cost price (per base unit)" type="number" min={0} step="0.01" {...register("cost_price")} error={errors.cost_price} hint="Used to check margins on quotes" />
            <TextField label="Lowest margin %" type="number" min={0} max={100} step="0.5" placeholder="Company default" {...register("min_margin_pct")} error={errors.min_margin_pct} />
          </div>
          <div>
            <p className="label">Other selling units</p>
            {unitRows.map((u, i) => (
              <div key={i} className="mb-2 flex items-center gap-2 text-sm">
                <span>1</span>
                <input className="input w-24" aria-label={`Unit ${i + 1} name`} placeholder="box" value={u.uom} onChange={(e) => setUnits(unitRows.map((x, j) => (j === i ? { ...x, uom: e.target.value } : x)))} />
                <span>=</span>
                <input className="input w-24 text-right" type="number" min={0} step="any" aria-label={`Unit ${i + 1} size`} value={u.factor} onChange={(e) => setUnits(unitRows.map((x, j) => (j === i ? { ...x, factor: e.target.value } : x)))} />
                <span className="text-muted">base units</span>
                <button type="button" className="btn-ghost px-2" aria-label={`Remove unit ${i + 1}`} onClick={() => setUnits(unitRows.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button type="button" className="btn-ghost text-xs" onClick={() => setUnits([...unitRows, { uom: "", factor: "" }])}><Icon name="plus" />Add unit</button>
            <p className="mt-1 text-xs text-muted">For example 1 box = 50 pcs. Quotes can then sell by the box at 50 times the unit price.</p>
          </div>
        </fieldset>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onDone}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={save.isPending}>{product ? "Save changes" : "Add product"}</button>
      </div>
    </form>
  );
}

export function ProductsPage() {
  const perms = useMe().data?.permissions ?? [];
  const terms = useCatalogTerms();
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 250); return () => clearTimeout(t); }, [search]);
  const products = useProducts(q);
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const canEdit = can(perms, "products:update");
  const addButton = can(perms, "products:create") && <button type="button" className="btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />Add product</button>;

  return (
    <>
      <PageHeader title="Products" subtitle={`Your catalog with units, ${terms.code}s, prices and ${terms.tax} rates. Quotes pick from here.`}
        actions={<>{can(perms, "data:export") && <button type="button" className="btn-ghost" onClick={() => void downloadExport("product")}><Icon name="download" />Export</button>}{addButton}</>} />
      <input type="search" className="input mb-4 max-w-sm" placeholder={`Search name, SKU, ${terms.code} or category`} aria-label="Search products" value={search} onChange={(e) => setSearch(e.target.value)} />
      {products.isLoading && <Spinner />}
      {products.data?.items.length === 0 && (q ? <EmptyState title="No matches" body={`No products match “${q}”.`} />
        : <EmptyState title="No products yet" body="Add the parts and assemblies you quote, or import your item master from a spreadsheet." action={addButton} />)}
      {!!products.data?.items.length && (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
              <tr><th className="px-4 py-3">Product</th><th className="px-4 py-3">{terms.code}</th><th className="px-4 py-3">Unit</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-right">{terms.tax}</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {products.data.items.map((p) => (
                <tr key={p.id} className={`border-b border-line last:border-0 ${canEdit ? "cursor-pointer hover:bg-canvas" : ""}`} onClick={() => canEdit && setEditing(p)}>
                  <td className="px-4 py-3"><span className="font-medium">{p.name}</span><span className="block text-xs text-muted">{p.sku}{p.category && ` · ${p.category}`}</span></td>
                  <td className="px-4 py-3 text-muted">{p.hsn_code}</td>
                  <td className="px-4 py-3">{p.uom}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums">{money(p.unit_price, p.currency)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.tax_rate}%</td>
                  <td className="px-4 py-3">{p.is_active ? <Badge tone="ok">Active</Badge> : <Badge>Inactive</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <Modal title={editing === "new" ? "Add product" : `Edit ${editing.name}`} onClose={() => setEditing(null)}>
          <ProductForm product={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />
        </Modal>
      )}
    </>
  );
}
